import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Chip,
} from "@mui/material";
import {
  api,
  useGetCustomersQuery,
  usePostUpdateStatusMutation,
} from "../../state/api";
import Header from "../../components/Header";
import { DataGrid } from "@mui/x-data-grid";
import { format } from "date-fns";
import SendIcon from "@mui/icons-material/Send";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Switch from "@mui/material/Switch";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { deleteCustomerAction } from "../../redux/actions/userAction";
import { getCustomerProfileActionFn } from "../../redux/actions/userAction";
import { useDispatch } from "react-redux";
import VerifiedIcon from "@mui/icons-material/Verified";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import axios from "axios";
import "../../index.css";

const mediaUrl = (path) => {
  if (!path) return undefined;
  if (/^https?:\/\//i.test(path)) return path;
  return `${axios.defaults.baseURL}/${path}`;
};

const RatingStars = ({ rating, color }) => {
  const safeRating = Number(rating) || 0;
  const filledStars = Math.floor(safeRating);
  const hasHalfStar = safeRating % 1 !== 0;

  const stars = [];

  // Add filled stars
  for (let i = 0; i < filledStars; i++) {
    stars.push(<StarIcon key={i} sx={{ color }} />);
  }

  // Add half star if applicable
  if (hasHalfStar) {
    stars.push(<StarHalfIcon key={filledStars} sx={{ color }} />);
  }

  // Add remaining empty stars
  const remainingStars = 5 - stars.length;
  for (let i = 0; i < remainingStars; i++) {
    stars.push(<StarOutlineIcon key={filledStars + i} sx={{ color }} />);
  }

  return (
    <>
      {stars.map((star, index) => (
        <span key={index}>{star}</span>
      ))}
    </>
  );
};

const User = () => {
  const dispatch = useDispatch();

  // values to be sent to the backend
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState({});
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [kycFilter, setKycFilter] = useState("all");

  const theme = useTheme();
  const { data } = useGetCustomersQuery({
    page,
    pageSize,
    sort: JSON.stringify(sort),
    search,
  });

  const [rowData, setRowData] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (data) {
      setRowData(data);
    }
  }, [data]);

  let [filterData, setFilterData] = useState([]);
  const [filterSelected, setFilterSelected] = useState(false);

  const handleFilterChange = (event) => {
    setKycFilter(event.target.value);
  };

  const fetchData = async () => {
    try {
      const response = await axios.get("/user/userFullname");
      setUsers(response.data.profile);
      setIsDataFetched(true);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleOpen = () => {
    if (!isDataFetched) {
      fetchData();
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get("/user/list", {
        params: {
          ...filterData,
        },
      });
      console.log("Response", response);
      let filteredUsers = response.data;

      if (kycFilter === "verified") {
        filteredUsers = filteredUsers.filter((user) =>
          user.kycDetails.some((detail) => detail.isVerified)
        );
      } else if (kycFilter === "notVerified") {
        filteredUsers = filteredUsers.filter(
          (user) => !user.kycDetails.some((detail) => detail.isVerified)
        );
      }

      setRowData(filteredUsers);
      // setFilterSelected(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (event, type) => {
    const value = event.target.value;
    if (value === "all") {
      setFilterData((prevFilters) => {
        const updatedFilters = { ...prevFilters };
        delete updatedFilters[type];
        return updatedFilters;
      });
    } else {
      setFilterData((prevFilters) => ({
        ...prevFilters,
        [type]: value,
      }));
    }
  };

  const handleEdit = async (userId) => {
    try {
      navigate(`/user/edit/${userId}`);
    } catch (error) {
      console.log("Error navigating to edit", error);
      toast.error("Error navigating to edit");
    }
  };

  const handleDelete = async (_id) => {
    try {
      const success = await dispatch(deleteCustomerAction(_id));
      if (success) {
        toast.success("Customer deleted successfully");
        dispatch(api.util.invalidateTags(["Customers"]));
      } else {
        toast.error("Failed to delete customer");
      }
    } catch (error) {
      console.error("Error Deleting Customer:", error);
    }
  };

  const removeCustomer = (_id) => {
    Swal.fire({
      title: "Confirm Removal",
      text: "Are you sure you want to remove this ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, remove it!",
    }).then((result) => {
      if (result.isConfirmed) {
        handleDelete(_id);
      }
    });
  };

  const [updateStatus] = usePostUpdateStatusMutation();

  const handleStatus = async (Id, isActive) => {
    const tag = "User";
    try {
      const response = await updateStatus({ Id, tag, isActive });
      // console.log(response);
      if (response.data.success) {
        setRowData((prevData) =>
          prevData.map((row) =>
            row._id === Id ? { ...row, isActive: !isActive } : row
          )
        );
        toast.success("Status updated successfully");
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const navigate = useNavigate();

  const navigateCreate = () => {
    navigate("/user/create");
  };

  const columns = [
    {
      field: "fullname",
      headerName: "FullName",
      flex: 0.6,
    },
    {
      field: "username",
      headerName: "Username",
      flex: 0.6,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 0.7,
    },
    {
      field: "mobileNumber",
      headerName: "Phone Number",
      flex: 0.6,
    },
    {
      field: "profession",
      headerName: "Profession",
      flex: 0.6,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "gender",
      headerName: "Gender",
      flex: 0.4,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "status",
      headerName: "status",
      flex: 0.6,
    },
    {
      field: "createdAt",
      headerName: "createdAt",
      flex: 0.6,
      renderCell: (params) => {
        const date = new Date(params.value);
        return format(date, "yyyy-MM-dd");
      },
    },
    {
      field: "isActive",
      headerName: "IsActive?",
      flex: 0.5,
      renderCell: (params) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Switch
            checked={params.value}
            onChange={() => handleStatus(params.row._id, params.row.isActive)}
            inputProps={{ "aria-label": "toggle status" }}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "green",
                "&:hover": {
                  backgroundColor: "rgba(0, 128, 0, 0.50)",
                },
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "green",
              },
              "& .MuiSwitch-switchBase": {
                color: "red",
                "&:hover": {
                  backgroundColor: "rgba(255, 0, 0, 0.5)",
                },
              },
              "& .MuiSwitch-switchBase + .MuiSwitch-track": {
                backgroundColor: "red",
              },
            }}
            color="default"
          />
          {params.value ? "T" : "F"}
        </div>
      ),
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      flex: 0.8,
      minWidth: 220,
      renderCell: (params) => (
        <div onClick={(e) => e.stopPropagation()}>
          <IconButton color="info" onClick={() => handleEdit(params.row._id)}>
            <EditIcon />
          </IconButton>
          <IconButton
            variant="contained"
            style={{
              color:
                params.row.kycDetails &&
                params.row.kycDetails.length > 0 &&
                params.row.kycDetails[0].isVerified
                  ? "green"
                  : "white",
            }}
            onClick={() => verifyKYC(params.row)}
          >
            <VerifiedIcon size="large" />
          </IconButton>
          <IconButton
            color="success"
            onClick={() => profileView(params.row._id)}
          >
            <AccountCircleIcon />
          </IconButton>
          <IconButton
            color="primary"
            title="View full details"
            onClick={() => navigate(`/user/details/${params.row._id}`)}
          >
            <VisibilityIcon />
          </IconButton>
          <IconButton onClick={() => removeCustomer(params.row._id)}>
            <DeleteIcon sx={{ color: "red" }} />
          </IconButton>
        </div>
      ),
    },
  ];

  const [isModalOpen, setModalOpen] = useState(false);
  const [isProfileModelOpen, setProfileModelOpen] = useState(false);
  const [profile, setProfile] = useState(null);

  const verifyKYC = async (user) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  const profileView = async (userId) => {
    try {
      const response = await dispatch(getCustomerProfileActionFn(userId));
      console.log("Response:", response);
      if (response.response && response.success) {
        const userProfile = response.response[0];
        setProfile(userProfile);
        setProfileModelOpen(true);
      } else {
        console.error("Error fetching profile:", response.error);
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  const handleKycVerify = async (status) => {
    try {
      const userId = selectedUser._id;
      const response = await axios.post(`/kyc/verify?userId=${userId}`, {
        status: status,
      });

      if (response.status === 200) {
        if (response.data.success) {
          toast.success(response.data.message);
        } else if (response.data.message === "Already verified") {
          toast.info("KYC is already verified");
        }
        setModalOpen(false);
      }
      if (status === "false") {
        toast.info("KYC is rejected");
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error verifying KYC:", error);
      toast.error("Failed to verify KYC");
    }
  };

  return (
    <Box m="1.5rem 2.5rem">
      <ToastContainer />
      <Header title="USER MANAGEMENT" />
      <Box display="flex" flexWrap="wrap" alignItems="flex-end">
        <FormControl variant="standard" sx={{ m: 2, minWidth: 200 }}>
          <InputLabel id="select-standard-label">Verified KYC</InputLabel>
          <Select
            id="select-standard"
            onChange={handleFilterChange}
            label="verified kyc"
            value={kycFilter}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="verified">Verified</MenuItem>
            <MenuItem value="notVerified">Not Verified</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="standard" sx={{ m: 2, minWidth: 200 }}>
          <InputLabel id="select-standard-label">user</InputLabel>
          <Select
            id="select-standard"
            onChange={(e) => handleChange(e, "userId")}
            onOpen={handleOpen}
            label="user"
          >
            <MenuItem value="all">All</MenuItem>
            {isDataFetched &&
              users.map((user) => (
                <MenuItem key={user.fullname} value={user._id}>
                  {user.fullname}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl variant="standard" sx={{ m: 2, minWidth: 200 }}>
          <InputLabel id="select-standard-label">Email</InputLabel>
          <Select
            id="select-standard"
            onChange={(e) => handleChange(e, "email")}
            onOpen={handleOpen}
            label="Email"
          >
            <MenuItem value="all">All</MenuItem>
            {isDataFetched &&
              users.map((user) => (
                <MenuItem key={user._id} value={user.email}>
                  {user.email}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl variant="standard" sx={{ m: 2, minWidth: 200 }}>
          <InputLabel id="select-standard-label">Mobile Number</InputLabel>
          <Select
            id="select-standard"
            onChange={(e) => handleChange(e, "mobileNumber")}
            onOpen={handleOpen}
            label="Mobile Number"
          >
            <MenuItem value="all">All</MenuItem>
            {isDataFetched &&
              users.map((user) => (
                <MenuItem key={user._id} value={user.mobileNumber}>
                  {user.mobileNumber}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl variant="standard" sx={{ m: 2, minWidth: 100 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SendIcon />}
            onClick={handleSubmit}
          >
            Filter
          </Button>
        </FormControl>
        <Button
          variant="contained"
          endIcon={<AddCircleIcon />}
          onClick={navigateCreate}
          sx={{ m: 2, ml: "auto" }}
        >
          Add User
        </Button>
      </Box>
      {/* {filterSelected && ( */}
      <Box
        mt="40px"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.success,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.secondary[200]} !important`,
          },
        }}
      >
        <DataGrid
          // loading={isLoading || !data}
          getRowId={(row) => row._id}
          rows={rowData}
          columns={columns}
          onRowClick={(params) => navigate(`/user/details/${params.row._id}`)}
          sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
          autoHeight
          disableColumnMenu
          disableColumnSelector
          hideFooter
          // initialState={{
          //   pinnedColumns: { right: ["actions"] },
          // }}
          rowCount={(rowData && rowData.total) || 0}
          rowsPerPageOptions={[20, 50, 100]}
          pagination
          page={page}
          pageSize={pageSize}
          paginationMode="server"
          sortingMode="server"
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          onSortModelChange={(newSortModel) => setSort(...newSortModel)}
        />
      </Box>
      {/* )} */}

      {/* Kyc Verify Model Box */}
      <Dialog
        open={isModalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="lg"
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
          },
        }}
      >
        <Box display="flex" justifyContent="flex-end">
          <IconButton color="error" onClick={() => setModalOpen(false)}>
            <CancelIcon />
          </IconButton>
        </Box>
        <DialogTitle variant="h2">Verify KYC</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Left side: User details */}
            <Grid item xs={6}>
              <Typography variant="h4" sx={{ marginBottom: "16px" }}>
                User Details:
              </Typography>
              <Typography variant="h5" sx={{ marginBottom: "10px" }}>
                Full Name: {selectedUser && selectedUser.fullname}
              </Typography>
              <Typography variant="h5" sx={{ marginBottom: "10px" }}>
                Gender: {selectedUser && selectedUser.gender}
              </Typography>
              <Typography variant="h5" sx={{ marginBottom: "10px" }}>
                dob: {selectedUser && selectedUser.dob}
              </Typography>
              <img
                src={mediaUrl(selectedUser && selectedUser.profilePicture)}
                alt="Profile"
                className="kyc-image"
              />
            </Grid>

            {/* Right side: KYC details */}
            <Grid item xs={6}>
              <Typography variant="h4" sx={{ marginBottom: "16px" }}>
                KYC Details
              </Typography>
              {selectedUser &&
                selectedUser.kycDetails &&
                selectedUser.kycDetails.map((kycDetail, index) => (
                  <div key={index}>
                    <Typography
                      variant="h5"
                      sx={{
                        color: kycDetail.isVerified ? "green" : "red",
                        marginBottom: "10px",
                      }}
                    >
                      Is Verified: {kycDetail.isVerified ? "Yes" : "No"}
                    </Typography>
                    <Typography variant="h5">
                      Document-Type: {kycDetail.idType}
                    </Typography>
                    <Typography variant="h5">
                      Document-No: {kycDetail.idNumber}
                    </Typography>
                    <img
                      src={mediaUrl(kycDetail.ppSizePhoto)}
                      alt="PP Size Photo"
                      className="kyc-image"
                    />
                    <img
                      src={mediaUrl(kycDetail.idFront)}
                      alt="Citizenship Front"
                      className="kyc-image"
                    />
                    <img
                      src={mediaUrl(kycDetail.idBack)}
                      alt="Citizenship Back"
                      className="kyc-image"
                    />
                  </div>
                ))}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleKycVerify("false")}
            sx={{
              color: "red",
              "&:hover": { background: "darkred" },
              fontSize: "0.8rem",
              padding: "12px 24px",
            }}
          >
            Reject
          </Button>
          <Button
            onClick={() => handleKycVerify("true")}
            color="primary"
            sx={{
              color: "green",
              "&:hover": { backgroundColor: "darkgreen" },
              fontSize: "0.8rem",
              padding: "12px 24px",
            }}
          >
            Verify
          </Button>
        </DialogActions>
      </Dialog>

      {/* Profile View */}
      <Dialog
        open={isProfileModelOpen}
        onClose={() => setProfileModelOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
          },
        }}
      >
        <Box display="flex" justifyContent="flex-end" pt={1} pr={1}>
          <IconButton color="error" onClick={() => setProfileModelOpen(false)}>
            <CancelIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ pt: 0 }}>
          <Box display="flex" flexDirection="column" alignItems="center" mb="1.5rem">
            <Avatar
              src={mediaUrl(profile && profile.profilePicture)}
              alt={profile && profile.fullname}
              sx={{
                width: 96,
                height: 96,
                mb: "0.75rem",
                border: `2px solid ${theme.palette.secondary[250] || "#a370d1"}`,
              }}
            >
              {profile && profile.fullname ? profile.fullname.charAt(0).toUpperCase() : "?"}
            </Avatar>
            <Typography variant="h4" fontWeight="bold">
              {(profile && profile.fullname) || "—"}
            </Typography>
            <Typography sx={{ color: theme.palette.secondary[200] }}>
              @{(profile && profile.username) || "—"}
            </Typography>
            <Box display="flex" gap="0.5rem" mt="0.5rem">
              <Chip
                size="small"
                label={(profile && profile.status) || "unknown"}
                sx={{
                  textTransform: "capitalize",
                  backgroundColor: "#7a7f9d22",
                  color: "#a370d1",
                  fontWeight: 600,
                }}
              />
              {profile && profile.kycInfo && profile.kycInfo.length > 0 && (
                <Chip
                  size="small"
                  label={profile.kycInfo[0].isVerified ? "KYC Verified" : "KYC Pending"}
                  sx={{
                    backgroundColor: profile.kycInfo[0].isVerified ? "#00c9a722" : "#ffda8522",
                    color: profile.kycInfo[0].isVerified ? "#00c9a7" : "#ffda85",
                    fontWeight: 600,
                  }}
                />
              )}
              <RatingStars rating={profile && profile.userRating} color="#ffda85" />
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography sx={{ color: theme.palette.secondary[200], fontSize: "0.75rem" }}>
                EMAIL
              </Typography>
              <Typography sx={{ mb: "1rem", wordBreak: "break-word" }}>
                {(profile && profile.email) || "—"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ color: theme.palette.secondary[200], fontSize: "0.75rem" }}>
                MOBILE
              </Typography>
              <Typography sx={{ mb: "1rem" }}>
                {(profile && profile.mobileNumber) || "—"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ color: theme.palette.secondary[200], fontSize: "0.75rem" }}>
                CITIZENSHIP NO
              </Typography>
              <Typography sx={{ mb: "1rem" }}>
                {profile && profile.kycInfo && profile.kycInfo.length > 0
                  ? profile.kycInfo[0].idNumber
                  : "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ color: theme.palette.secondary[200], fontSize: "0.75rem" }}>
                USER PRICE
              </Typography>
              <Typography sx={{ mb: "1rem" }}>
                {profile && profile.price ? `NRs ${profile.price}` : "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ color: theme.palette.secondary[200], fontSize: "0.75rem" }}>
                CREDIT POINTS
              </Typography>
              <Typography sx={{ mb: "1rem" }}>
                {profile && profile.walletInfo && profile.walletInfo.length > 0
                  ? profile.walletInfo[0].creditPoints
                  : "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ color: theme.palette.secondary[200], fontSize: "0.75rem" }}>
                REWARD POINTS
              </Typography>
              <Typography sx={{ mb: "1rem" }}>
                {profile && profile.walletInfo && profile.walletInfo.length > 0
                  ? profile.walletInfo[0].rewardPoints
                  : "N/A"}
              </Typography>
            </Grid>
          </Grid>

          {profile && profile.kycInfo && profile.kycInfo.length > 0 && profile.kycInfo[0].ppSizePhoto && (
            <Box mt="0.5rem">
              <Typography sx={{ color: theme.palette.secondary[200], fontSize: "0.75rem", mb: "0.5rem" }}>
                KYC PHOTO
              </Typography>
              <img
                src={mediaUrl(profile.kycInfo[0].ppSizePhoto)}
                alt="KYC document"
                className="circular-image"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setProfileModelOpen(false)}
            variant="contained"
            sx={{ fontSize: "0.8rem", padding: "10px 24px" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default User;
